import { useMemo } from 'react';
import type { FC } from 'react';

import { Box, Table, TableBody, TableCell, tableCellClasses, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { Country } from 'src/api/zrm';
import { formatCount, formatKr, formatPercent, formatShortVolume } from 'src/tv/dashboardFormat';
import { Figure, GoalHeadline, monthName, NUMERIC, NumCell, podiumRowClass, RankChip, tableSx } from 'src/tv/dashboardParts';
import type { MbDashboard as MbDashboardData, MbTotals, TvDashboardSnapshot } from 'src/tv/dashboardTypes';
import { mbView } from 'src/tv/dashboardView';
import type { MbView } from 'src/tv/dashboardView';
import { TV_COLORS, TV_PODIUM } from 'src/tv/tvTheme';
import WeekChart from 'src/tv/WeekChart';

interface TodayFiguresProps {
  today: MbTotals;
  country: Country;
}

const TodayFigures: FC<TodayFiguresProps> = (props) => {
  const { today, country } = props;
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '0.9fr 1.5fr 0.8fr', columnGap: '1.6vw', alignContent: 'start' }}>
      <Figure
        label={t('Sent today')}
        value={formatCount(today.utskick)}
        detail={formatShortVolume(today.utskick_volume, country)}
      />
      <Figure
        label={t('Helped today (blanco)')}
        value={formatCount(today.helped)}
        detail={formatShortVolume(today.helped_volume, country)}
      />
      <Figure
        label={t('Instant')}
        value={formatCount(today.instant)}
      />
    </Box>
  );
};

interface ChartProps {
  view: MbView;
  country: Country;
}

/** Month-end projection next to the month goals: helped, average loan, volume, all blanco only. */
const RunratePlan: FC<ChartProps> = (props) => {
  const { view, country } = props;
  const { t } = useTranslation();
  const { plan } = view;

  const rows = [
    { label: t('Helped'), runrate: formatCount(plan.helped.runrate), goal: formatCount(plan.helped.goal), isMet: plan.helped.isMet },
    { label: t('Average loan'), runrate: formatKr(plan.averageLoan.runrate), goal: formatKr(plan.averageLoan.goal), isMet: plan.averageLoan.isMet },
    {
      label: t('Volume'),
      runrate: formatShortVolume(plan.volume.runrate, country),
      goal: formatShortVolume(plan.volume.goal, country),
      isMet: plan.volume.isMet,
    },
  ];

  return (
    <Table
      size="small"
      sx={{
        alignSelf: 'start',
        [`& .${tableCellClasses.root}`]: {
          py: '0.35vh',
          px: 0,
          border: 'none',
          lineHeight: 'inherit',
          textAlign: 'right',
          whiteSpace: 'nowrap',
          ...NUMERIC,
        },
        [`& .${tableCellClasses.head}`]: { fontSize: '1vw', fontWeight: 600, color: TV_COLORS.muted, pt: 0 },
        '& th:first-of-type, & td:first-of-type': { textAlign: 'left', fontSize: '1.15vw', fontWeight: 600, color: TV_COLORS.muted },
        [`& .${tableCellClasses.body}`]: { borderTop: `1px solid ${TV_COLORS.line}` },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell>{t('Month-end (blanco)')}</TableCell>
          <TableCell>{t('Runrate')}</TableCell>
          <TableCell>{t('Goal')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell>{row.label}</TableCell>
            <TableCell sx={{ fontSize: '1.5vw', fontWeight: 700, color: row.isMet ? TV_COLORS.win : TV_COLORS.text }}>
              {row.runrate}
            </TableCell>
            <TableCell sx={{ fontSize: '1.2vw', color: TV_COLORS.muted, pl: '1.4vw !important' }}>
              {row.goal}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface SellerTableProps {
  view: MbView;
  month: string;
}

/**
 * Ranked by this month's helped blanco + mortgage (zrm's `helped_total`, as the old sheet did), while the Helped
 * columns are blanco only; the header says both, so the Helped column is not read as the sort key.
 */
const SellerTable: FC<SellerTableProps> = (props) => {
  const { view, month } = props;
  const { t } = useTranslation();
  const { sellers, totals } = view;

  if (!sellers.length)
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px solid ${TV_COLORS.line}`, mt: '3vh' }}>
        <Typography sx={{ fontSize: '1.6vw', color: TV_COLORS.muted }}>
          {t('No applications this month yet.')}
        </Typography>
      </Box>
    );

  const cells = (row: MbTotals, part: 'today' | 'month') => (part === 'today'
    ? [
      { value: row.apps, text: formatCount(row.apps), className: 'group-start' },
      { value: row.sameday, text: formatCount(row.sameday) },
      { value: row.instant, text: formatCount(row.instant) },
      { value: row.helped, text: formatCount(row.helped) },
      { value: row.helped_volume, text: formatKr(row.helped_volume) },
    ]
    : [
      { value: row.apps, text: formatCount(row.apps), className: 'group-start' },
      { value: row.sameday, text: formatCount(row.sameday) },
      { value: row.instant, text: formatCount(row.instant) },
      { value: row.utskick, text: formatCount(row.utskick) },
      { value: row.helped, text: formatCount(row.helped), className: 'strong' },
      { value: row.helped_volume, text: formatKr(row.helped_volume), className: 'strong' },
    ]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, mt: '2vh', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        data-testid="tv-mb-sellers"
        sx={tableSx('0.9vw')}
      >
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}>{t('Sellers')}</TableCell>
            <TableCell
              colSpan={5}
              className="group-start"
            >
              {t('Today')}
            </TableCell>
            <TableCell
              colSpan={9}
              className="group-start"
            >
              {month}
              <Box
                component="span"
                sx={{ ml: '0.8vw', fontWeight: 500, color: TV_COLORS.muted }}
              >
                {t('Ranked by helped incl. mortgages')}
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="rank" />
            <TableCell className="name" />
            <TableCell className="group-start">{t('Apps')}</TableCell>
            <TableCell>{t('Same day')}</TableCell>
            <TableCell>{t('Instant')}</TableCell>
            <TableCell>{t('Helped (blanco)')}</TableCell>
            <TableCell>{t('Volume')}</TableCell>
            <TableCell className="group-start">{t('Apps')}</TableCell>
            <TableCell>{t('Same day')}</TableCell>
            <TableCell>{t('Instant')}</TableCell>
            <TableCell>{t('Sent')}</TableCell>
            <TableCell>{t('Helped (blanco)')}</TableCell>
            <TableCell>{t('Volume')}</TableCell>
            <TableCell>{t('Return')}</TableCell>
            <TableCell>{t('Mortgages')}</TableCell>
            <TableCell>{t('Volume')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sellers.map((seller, index) => (
            <TableRow
              key={seller.user_id}
              className={podiumRowClass(index, TV_PODIUM.length, sellers.length)}
            >
              <TableCell className="rank"><RankChip rank={index + 1} /></TableCell>
              <TableCell className="name">{seller.name}</TableCell>
              {cells(seller.today, 'today').map((cell, column) => (
                <NumCell
                  key={`t${column}`}
                  {...cell}
                />
              ))}
              {cells(seller.month, 'month').map((cell, column) => (
                <NumCell
                  key={`m${column}`}
                  {...cell}
                />
              ))}
              <TableCell>{formatPercent(seller.return_rate)}</TableCell>
              <NumCell
                value={seller.month.helped_mortgage}
                text={formatCount(seller.month.helped_mortgage)}
                className="strong"
              />
              <NumCell
                value={seller.month.helped_mortgage_volume}
                text={formatKr(seller.month.helped_mortgage_volume)}
                className="strong"
              />
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="rank" />
            <TableCell className="name">
              {view.hiddenSellers ? t('Total, {{count}} more not shown', { count: view.hiddenSellers }) : t('Total')}
            </TableCell>
            {cells(totals.today, 'today').map((cell, column) => (
              <TableCell
                key={`t${column}`}
                className={cell.className}
              >
                {cell.text}
              </TableCell>
            ))}
            {cells(totals.month, 'month').map((cell, column) => (
              <TableCell
                key={`m${column}`}
                className={cell.className}
              >
                {cell.text}
              </TableCell>
            ))}
            <TableCell>{formatPercent(totals.returnRate)}</TableCell>
            <TableCell>{formatCount(totals.month.helped_mortgage)}</TableCell>
            <TableCell>{formatKr(totals.month.helped_mortgage_volume)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  );
};

interface MbDashboardProps {
  snapshot: TvDashboardSnapshot;
  mb: MbDashboardData;
}

/**
 * The meeting bookers' screen (replaces the "Monitor KA/SKA NO" sheet). Three columns read left to
 * right as today, this week and this month, each a result against its goal with the detail under
 * it; the seller leaderboard spans the bottom.
 */
const MbDashboard: FC<MbDashboardProps> = (props) => {
  const { snapshot, mb } = props;
  const { t } = useTranslation();

  // One view per snapshot: a re-render without new data (a status change above) hands the week chart the same
  // arrays, so chart.js does not repaint. The clock ticks inside DashboardStatus and does not re-render this.
  const view = useMemo(() => mbView(snapshot, mb), [snapshot, mb]);
  const { country } = snapshot;
  const month = monthName(snapshot.today, country);

  return (
    <Box
      data-testid="tv-mb-dashboard"
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: '1vh' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          columnGap: '3.2vw',
          rowGap: '2vh',
        }}
      >
        <GoalHeadline
          label={t('Applications today')}
          view={view.day}
        />
        <GoalHeadline
          label={t('Applications this week')}
          view={view.week}
        />
        <GoalHeadline
          label={t('Runrate {{month}}', { month })}
          view={view.month}
          caption={t('{{count}} so far · day {{worked}}/{{working}}', {
            count: formatCount(view.month.soFar),
            worked: snapshot.worked_days_in_month,
            working: snapshot.working_days_in_month,
          })}
        />

        <TodayFigures
          today={view.today}
          country={country}
        />
        <WeekChart
          view={view}
          country={country}
        />
        <RunratePlan
          view={view}
          country={country}
        />
      </Box>

      <SellerTable
        view={view}
        month={month}
      />
    </Box>
  );
};

export default MbDashboard;
